import React from "react";
import { Gavel, Scale, CheckCircle2, XCircle, AlertTriangle, FileText } from "lucide-react";

/**
 * Structured case-brief card shown at the top of an article
 * when article.article_type === "case_brief" OR article.case_brief is present.
 *
 * Expected shape on article:
 *   case_brief: {
 *     court: "Supreme Court of India",
 *     citation: "2024 SCC ...",
 *     parties: "Cox & Kings Ltd v. SAP India",
 *     outcome: "held" | "dismissed" | "overturned" | "partially allowed",
 *     issue: "short issue statement",
 *     ratio: "the governing legal principle",
 *   }
 */

const OUTCOME_TONE = {
  held: { color: "var(--io-green)", Icon: CheckCircle2, label: "Held / Allowed" },
  dismissed: { color: "var(--io-red)", Icon: XCircle, label: "Dismissed" },
  overturned: { color: "var(--io-amber)", Icon: AlertTriangle, label: "Overturned" },
  "partially allowed": { color: "var(--io-amber)", Icon: AlertTriangle, label: "Partially allowed" },
};

const Row = ({ label, value, mono }) => (
  <div className="py-2 grid grid-cols-3 gap-3 border-b border-[var(--io-border)] last:border-b-0">
    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--io-muted)]">{label}</dt>
    <dd className={`col-span-2 ${mono ? "font-mono text-[12px]" : "font-sans text-[14px]"} text-[var(--io-text)]`}>{value || "—"}</dd>
  </div>
);

export default function CaseBriefCard({ brief }) {
  if (!brief) return null;
  const outKey = (brief.outcome || "").toLowerCase();
  const out = OUTCOME_TONE[outKey] || { color: "var(--io-text-2)", Icon: FileText, label: brief.outcome || "—" };
  const OutIcon = out.Icon;

  return (
    <section className="io-card-el p-5 md:p-6 mb-10" data-testid="case-brief-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Gavel size={16} className="text-[var(--io-gold)]" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--io-gold)]">Case Brief</span>
        </div>
        <span className="io-badge inline-flex items-center gap-1.5" style={{ color: out.color, borderColor: out.color + "40", background: out.color + "15" }}>
          <OutIcon size={11} />
          {out.label}
        </span>
      </div>

      <dl>
        <Row label="Court / Tribunal" value={brief.court} />
        {brief.parties && <Row label="Parties" value={brief.parties} />}
        {brief.citation && <Row label="Citation" value={brief.citation} mono />}
        {brief.issue && <Row label="Issue" value={brief.issue} />}
        {brief.ratio && <Row label="Ratio" value={brief.ratio} />}
      </dl>
    </section>
  );
}
